import type { ParsedPrimer } from "@formats/primers";
import { useLiveQuery } from "dexie-react-hooks";
import type { GnomuxPrimer } from "./content";
import { db } from "./db";

export function useAllPrimers() {
  return useLiveQuery(() => db.primers.toArray());
}

export function usePrimers(sequenceId: string) {
  return useLiveQuery(
    () => db.primers.where("sequenceId").equals(sequenceId).toArray(),
    [sequenceId],
  );
}

export function usePrimer(id: string) {
  return useLiveQuery(() => db.primers.get(id), [id]);
}

export function useStandalonePrimers() {
  return useLiveQuery(() =>
    db.primers.filter((primer) => primer.sequenceId === null).toArray(),
  );
}

export function useLibraryPrimers() {
  return useLiveQuery(() =>
    db.primers.filter((primer) => primer.inLibrary).toArray(),
  );
}

export async function addPrimer(
  primer: Omit<GnomuxPrimer, "id" | "createdAt">,
): Promise<string> {
  const id = crypto.randomUUID();
  await db.primers.add({
    ...primer,
    id,
    createdAt: new Date(),
  });
  return id;
}

export async function updatePrimer(
  id: string,
  updates: Partial<Omit<GnomuxPrimer, "id" | "createdAt">>,
): Promise<void> {
  await db.primers.update(id, updates);
}

export async function deletePrimer(id: string): Promise<void> {
  await db.primers.delete(id);
}

export async function bulkAddPrimers(
  primers: ParsedPrimer[],
  ownerId: string,
): Promise<number> {
  const now = new Date();
  const records: GnomuxPrimer[] = primers.map((p) => ({
    id: crypto.randomUUID(),
    name: p.name,
    sequence: p.sequence,
    extension: "",
    tm: p.tm,
    gcPercent: p.gcPercent,
    direction: p.direction,
    sequenceId: null,
    start: null,
    inLibrary: true,
    createdAt: now,
    ownerId,
  }));

  await db.transaction("rw", db.primers, async () => {
    await db.primers.bulkAdd(records);
  });

  return records.length;
}

export async function getFileIdForSequence(
  sequenceId: string,
): Promise<string | null> {
  const file = await db.objects.where("content").equals(sequenceId).first();
  return file?.id?.toString() ?? null;
}
